import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from "@repo/ui";
import { Html5Qrcode } from "html5-qrcode";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import AdminNav from "@/pages/admin/admin-nav";

export default function AdminScanner() {
  const navigate = useNavigate();
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [manualId, setManualId] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    document.title = "QR Scanner | House & Condo Expo";
  }, []);

  // Initialize scanner
  useEffect(() => {
    scannerRef.current = new Html5Qrcode("qr-reader");
    return () => {
      // Cleanup on unmount
      if (scannerRef.current?.isScanning) {
        scannerRef.current.stop().catch(() => {
          // Ignore errors during cleanup
        });
      }
    };
  }, []);

  // Handle QR code result
  useEffect(() => {
    if (!result) return;

    // Extract ID from URL if it's a full URL (e.g., https://house-condo-expo.stardeck.site/lookup?id=uuid-or-legacy-uid)
    let id: string;
    try {
      const url = new URL(result);
      const idParam = url.searchParams.get("id");
      id = idParam || result;
    } catch {
      // Not a valid URL, use as-is (could be UUID, legacy_uid, or numeric ID)
      id = result;
    }

    if (!id || !id.trim()) return;
    navigate(`/admin/registrations/${id}`);
  }, [result, navigate]);

  const startScanning = async () => {
    if (!scannerRef.current) return;
    setScanError(null);

    try {
      await scannerRef.current.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        (decodedText) => {
          setResult(decodedText);
          stopScanning();
        },
        () => {
          // Ignore frame processing errors
        }
      );
      setScanning(true);
    } catch (err) {
      setScanError("ไม่สามารถเริ่มกล้องได้ กรุณาตรวจสอบสิทธิ์การเข้าถึง");
      console.error("Scanner error:", err);
    }
  };

  const stopScanning = async () => {
    if (!scannerRef.current?.isScanning) return;

    try {
      await scannerRef.current.stop();
      setScanning(false);
    } catch (err) {
      console.error("Error stopping scanner:", err);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!scannerRef.current) return;
    setScanError(null);

    try {
      const decodedText = await scannerRef.current.scanFile(file, false);
      setResult(decodedText);
    } catch (err) {
      setScanError("ไม่พบ QR Code ในรูปภาพ");
      console.error("File scan error:", err);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const onManualLookup = async () => {
    setResult(manualId);
  };

  const onPhoneLookup = async () => {
    if (!phone.trim()) {
      setLookupError("กรุณากรอกเบอร์โทรศัพท์");
      return;
    }
    setLookupError(null);
    setLookupLoading(true);
    try {
      const res = await fetch(
        `/api/registrations/lookup?phone=${encodeURIComponent(phone.trim())}`
      );
      if (!res.ok) {
        setLookupError("ไม่พบข้อมูล");
        return;
      }
      const data = (await res.json()) as { uuid: string };
      navigate(`/admin/registrations/${data.uuid}`);
    } catch {
      setLookupError("การค้นหาล้มเหลว");
    } finally {
      setLookupLoading(false);
    }
  };

  const onEmailLookup = async () => {
    if (!email.trim()) {
      setLookupError("กรุณากรอกอีเมล");
      return;
    }
    setLookupError(null);
    setLookupLoading(true);
    try {
      const res = await fetch(
        `/api/registrations/lookup?email=${encodeURIComponent(email.trim())}`
      );
      if (!res.ok) {
        setLookupError("ไม่พบข้อมูล");
        return;
      }
      const data = (await res.json()) as { uuid: string };
      navigate(`/admin/registrations/${data.uuid}`);
    } catch {
      setLookupError("การค้นหาล้มเหลว");
    } finally {
      setLookupLoading(false);
    }
  };

  return (
    <div>
      <AdminNav />
      <div className="p-6 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>QR Scanner</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div id="qr-reader" className="w-full max-w-lg mx-auto" />
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  onClick={() => {
                    if (scanning) {
                      stopScanning();
                    } else {
                      startScanning();
                    }
                  }}
                >
                  {scanning ? "หยุด" : "เริ่ม"} กล้อง
                </Button>
                <label className="inline-flex">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleFileUpload(file);
                      }
                    }}
                  />
                  <Button asChild variant="outline">
                    <span>อัปโหลดรูปภาพ</span>
                  </Button>
                </label>
                {result && <div className="text-sm text-muted-foreground">สแกนแล้ว: {result}</div>}
              </div>
              {scanError && <p className="text-sm text-destructive">{scanError}</p>}
            </div>

            <div className="space-y-4 border-t pt-4">
              <div className="space-y-2">
                <Label htmlFor="manualId">ค้นหาด้วย UUID หรือ Legacy UID</Label>
                <div className="flex gap-2">
                  <Input
                    id="manualId"
                    value={manualId}
                    onChange={(e) => setManualId(e.target.value)}
                    placeholder="กรอก UUID หรือ UID"
                    className="max-w-xs"
                  />
                  <Button onClick={onManualLookup}>ค้นหา</Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">ค้นหาด้วยเบอร์โทรศัพท์</Label>
                <div className="flex gap-2">
                  <Input
                    id="phone"
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value);
                      setLookupError(null);
                    }}
                    placeholder="กรอกเบอร์โทรศัพท์"
                    className="max-w-xs"
                  />
                  <Button onClick={onPhoneLookup} disabled={lookupLoading}>
                    {lookupLoading ? "กำลังค้นหา..." : "ค้นหา"}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">ค้นหาด้วยอีเมล</Label>
                <div className="flex gap-2">
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setLookupError(null);
                    }}
                    placeholder="กรอกอีเมล"
                    className="max-w-xs"
                  />
                  <Button onClick={onEmailLookup} disabled={lookupLoading}>
                    {lookupLoading ? "กำลังค้นหา..." : "ค้นหา"}
                  </Button>
                </div>
              </div>

              {lookupError && <p className="text-sm text-destructive">{lookupError}</p>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
